import { Link } from 'react-router-dom';
import { type ReactNode } from 'react';

interface ArticleSEO {
  title: string;
  description: string;
  keywords: string;
  canonicalPath: string;
  ogType?: 'article' | 'website';
  ogImage?: string;
}

interface ArticleLayoutProps {
  title: string;
  subtitle?: string;
  readTime: string;
  category: string;
  breadcrumbLabel: string;
  children: ReactNode;
  seo: ArticleSEO;
  jsonLd?: Record<string, unknown>;
}

const DEFAULT_OG_IMAGE = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/a9b8fc4c2048f16265644a3fc912425a.jpeg';
const SITE_URL = 'https://vigorbuyspread.hair';

export default function ArticleLayout({ title, subtitle, readTime, category, breadcrumbLabel, children, seo, jsonLd }: ArticleLayoutProps) {
  const canonicalUrl = `${SITE_URL}${seo.canonicalPath}`;
  const ogImage = seo.ogImage || DEFAULT_OG_IMAGE;

  return (
    <article className="pt-20 pb-16">
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:type" content={seo.ogType || 'article'} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      )}

      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-foreground-700 transition-colors cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line text-[10px]"></i>
          <span className="text-foreground-700 font-medium">{breadcrumbLabel}</span>
        </nav>

        <div className="mb-8">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold tracking-wider uppercase mb-4">
            {category}
          </span>
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-3 leading-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="text-foreground-600 text-sm md:text-base mb-4">{subtitle}</p>
          )}
          <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {readTime}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              Updated July 2026
            </span>
          </div>
        </div>

        <div className="prose-custom space-y-7 text-foreground-700 leading-relaxed">
          {children}
        </div>

        <div className="mt-12 pt-8 border-t border-background-200/70">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 rounded-xl bg-primary-50 border border-primary-200/60">
            <div>
              <p className="font-heading font-semibold text-foreground-900 text-sm mb-1">Ready to Start Exploring?</p>
              <p className="text-foreground-600 text-xs">Browse our complete category database and begin your organized product research journey.</p>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/" className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 font-semibold text-xs hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-home-line mr-1.5"></i>Back to Homepage
              </Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-5 py-2.5 rounded-lg bg-background-50 border border-background-200 text-foreground-700 font-semibold text-xs hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                Visit Main Website <i className="ri-external-link-line ml-1"></i>
              </a>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-3">Related Resources</h3>
            <div className="flex flex-wrap gap-2">
              <Link to="/" className="px-3 py-1.5 rounded-lg bg-background-100 text-foreground-600 text-xs hover:bg-background-200 transition-colors cursor-pointer">
                <i className="ri-home-line mr-1"></i>Homepage
              </Link>
              <Link to="/vigorbuy-spreadsheet-beginner-guide" className="px-3 py-1.5 rounded-lg bg-background-100 text-foreground-600 text-xs hover:bg-background-200 transition-colors cursor-pointer">
                Beginner Guide
              </Link>
              <Link to="/vigorbuy-spreadsheet-safety-guide" className="px-3 py-1.5 rounded-lg bg-background-100 text-foreground-600 text-xs hover:bg-background-200 transition-colors cursor-pointer">
                Safety Guide
              </Link>
              <Link to="/vigorbuy-spreadsheet-comparison-guide" className="px-3 py-1.5 rounded-lg bg-background-100 text-foreground-600 text-xs hover:bg-background-200 transition-colors cursor-pointer">
                Comparison Guide
              </Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-3 py-1.5 rounded-lg bg-primary-100 text-primary-700 text-xs hover:bg-primary-200 transition-colors cursor-pointer">
                <i className="ri-external-link-line mr-1"></i>Main Website
              </a>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}